import MainCard from 'components/MainCard';
import CusDataGrid from 'components/CusDataGrid';
import CusDrawer from 'components/CusDrawer';
import CusMultipleFilter from 'components/CusMultipleFilter';
import { Button1, Button2 } from 'components/Button';
import useMain from 'hooks/useMain';
import React, { useMemo, useState } from 'react';
import { Box, Stack, TextField, Typography, useTheme } from '@mui/material';
import { useGetApiUsersAppVersion, useGetApiUsersProj } from 'api/mic-service/users/users';
import { micService } from 'services/mic';
import { handleAlertMessage, handleAsyncCall } from 'utils/common';
import { appDeviceVersionColumns } from './columns';

const AppDeviceVersionList = () => {
  const { t, upperCaseLang } = useMain();
  const theme = useTheme();
  const [projId, setProjId] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [softTarget, setSoftTarget] = useState('');
  const [hardTarget, setHardTarget] = useState('');
  const [saving, setSaving] = useState(false);

  const appVersionParams = useMemo(() => (projId ? { projId } : undefined), [projId]);
  const {
    data: appVersionData,
    isLoading: isAppVersionLoading,
    refetch
  } = useGetApiUsersAppVersion(appVersionParams);
  const { data: projectData, isLoading: isProjectLoading } = useGetApiUsersProj();

  const rows = useMemo(() => (Array.isArray(appVersionData) ? appVersionData : []), [appVersionData]);
  const projects = useMemo(() => (Array.isArray(projectData) ? projectData : []), [projectData]);

  const projectOptions = useMemo(() => {
    return projects
      .filter((p) => p?.ProjId)
      .map((p) => {
        const label =
          p[`ContractDesc${upperCaseLang}`] ||
          p.ContractDescTC ||
          p.ContractDescSC ||
          p.ContractDescEN ||
          p.ProjName ||
          p.ProjId;
        return { value: p.ProjId, label };
      })
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [projects, upperCaseLang]);

  const projNameById = useMemo(() => {
    const map = {};
    projectOptions.forEach((p) => {
      map[String(p.value).toLowerCase()] = p.label;
    });
    return map;
  }, [projectOptions]);

  const selectedProjectLabel = useMemo(() => {
    if (!projId) return '';
    return projNameById[String(projId).toLowerCase()] || projId;
  }, [projId, projNameById]);

  const filters = useMemo(
    () => [
      {
        id: 'ProjId',
        label: t('PROJECT'),
        type: 'select',
        options: [{ label: t('ALL'), value: '' }, ...projectOptions],
        fieldProps: {
          placeholder: true,
          InputLabelProps: { shrink: false }
        }
      }
    ],
    [t, projectOptions]
  );

  const softFilled = softTarget.trim().length > 0;
  const hardFilled = hardTarget.trim().length > 0;
  const canSubmit = Boolean(projId) && softFilled !== hardFilled && !saving;

  const openDrawer = () => {
    setSoftTarget('');
    setHardTarget('');
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    if (saving) return;
    setDrawerOpen(false);
  };

  const handleBatchSetTargets = () => {
    if (!canSubmit) {
      handleAlertMessage({
        text: t('BATCH_SET_TARGET_XOR_HINT'),
        showCancelButton: false
      });
      return;
    }

    handleAsyncCall({
      service: micService.batchSetAppDeviceVersionTargets,
      params: {
        ProjId: projId,
        SoftTargetVersion: softFilled ? softTarget.trim() : null,
        HardTargetVersion: hardFilled ? hardTarget.trim() : null
      },
      setDetailBlock: setSaving,
      successCb: (response) => {
        const updated = response?.data?.Updated ?? rows.length;
        handleAlertMessage({
          text: t('BATCH_SET_TARGET_SUCCESS', { COUNT: updated }),
          icon: 'success',
          showCancelButton: false,
          cb: () => {
            setDrawerOpen(false);
            refetch();
          }
        });
      }
    });
  };

  const applyUpdateLocalData = ({ DeviceId, ProjId, UpdateLocalData, confirmText }) => {
    handleAlertMessage({
      title: confirmText,
      cb: (r) => {
        if (!r.isConfirmed) return;
        handleAsyncCall({
          service: micService.setUpdateLocalData,
          params: {
            ...(DeviceId ? { DeviceId } : {}),
            ...(ProjId ? { ProjId } : {}),
            UpdateLocalData
          },
          successCb: (response) => {
            const updated = response?.data?.Updated ?? 0;
            handleAlertMessage({
              text: t('BATCH_SET_TARGET_SUCCESS', { COUNT: updated }),
              icon: 'success',
              showCancelButton: false,
              cb: () => refetch()
            });
          }
        });
      }
    });
  };

  const handleUpdateLocalData = (row, nextValue) => {
    const deviceId = row?.DeviceId;
    if (!deviceId) return;
    applyUpdateLocalData({
      DeviceId: deviceId,
      UpdateLocalData: nextValue,
      confirmText: nextValue
        ? t('CONFIRM_UPDATE_LOCAL_DATA', { DEVICE_ID: deviceId })
        : t('CONFIRM_CLEAR_UPDATE_LOCAL_DATA', { DEVICE_ID: deviceId })
    });
  };

  const handleBatchUpdateLocalData = () => {
    if (!projId) return;
    applyUpdateLocalData({
      ProjId: projId,
      UpdateLocalData: true,
      confirmText: t('CONFIRM_BATCH_UPDATE_LOCAL_DATA', { PROJECT: selectedProjectLabel })
    });
  };

  return (
    <>
      <MainCard content={false} sx={{ mb: '32px' }}>
        <CusMultipleFilter
          filters={filters}
          handleSubmit={(data) => {
            setProjId(data?.ProjId || '');
          }}
        />
      </MainCard>
      <MainCard>
        <CusDataGrid
          showExport
          loading={isAppVersionLoading || isProjectLoading}
          disableSelectionOnClick
          getRowId={(row) => row.Id}
          rows={rows}
          columns={appDeviceVersionColumns({ t, projNameById, onUpdateLocalData: handleUpdateLocalData })}
          cusToolbarComponents={() =>
            projId ? (
              <Stack direction="row" spacing={1} sx={{ ml: [0, 2], mt: [2, 'auto'] }}>
                <Button1 text={t('BATCH_SET_TARGET_VERSION')} onClick={openDrawer} />
                <Button1 text={t('BATCH_UPDATE_LOCAL_DATA')} onClick={handleBatchUpdateLocalData} />
              </Stack>
            ) : null
          }
        />
      </MainCard>

      <CusDrawer open={drawerOpen} onClose={closeDrawer}>
        <Box p={3}>
          <Typography variant="h4" color={theme.palette.primary[400]} mb={1}>
            {t('BATCH_SET_TARGET_VERSION')}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            {selectedProjectLabel}
          </Typography>
          <Typography variant="body2" mb={2}>
            {t('BATCH_SET_TARGET_XOR_HINT')}
          </Typography>
          <Stack spacing={2}>
            <TextField
              label={t('SOFT_TARGET_VERSION')}
              value={softTarget}
              onChange={(e) => setSoftTarget(e.target.value)}
              disabled={hardFilled || saving}
              fullWidth
              size="small"
              placeholder="e.g. 1.0.30"
            />
            <TextField
              label={t('HARD_TARGET_VERSION')}
              value={hardTarget}
              onChange={(e) => setHardTarget(e.target.value)}
              disabled={softFilled || saving}
              fullWidth
              size="small"
              placeholder="e.g. 1.0.29"
            />
            <Stack direction="row" spacing={2} justifyContent="flex-end" pt={2}>
              <Button2 text={t('BTN_CANCEL')} onClick={closeDrawer} disabled={saving} />
              <Button1 text={t('BTN_SAVE')} onClick={handleBatchSetTargets} disabled={!canSubmit} />
            </Stack>
          </Stack>
        </Box>
      </CusDrawer>
    </>
  );
};

export default AppDeviceVersionList;
